import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { log } from '@app/shared/utils/logger';
import { telegramVibrate } from '@app/shared/utils/common';
import { MediaData } from '@app/shared/types/player';
import notification from '@app/shared/utils/notification';
import { LOCAL_STORAGE } from '@app/shared/constants/common';
import { useMediaCooldown } from '@app/shared/hooks/useMediaCooldown';
import { useClaimMediaRewardMutation } from '@app/store/slices/users/usersApi';

export const useAudioPlayer = (tracks: MediaData[], settlementId?: number) => {
  const audioRef = useRef<HTMLAudioElement>(null);
  const { t } = useTranslation();
  const [currentTrackIndex, setCurrentTrackIndex] = useState(0);

  const currentTrack =
    currentTrackIndex >= 0 || currentTrackIndex < tracks.length ? tracks[currentTrackIndex] : null;
  const { trigger } = useMediaCooldown(LOCAL_STORAGE.media);

  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isTrackFinished, setIsTrackFinished] = useState(false);

  const [claimReward] = useClaimMediaRewardMutation();

  const handleLoaded = () => {
    if (!audioRef.current) {
      return;
    }
    setDuration(audioRef.current.duration || 0);
  };

  const togglePlay = () => {
    telegramVibrate();
    const audio = audioRef.current;
    if (!audio) {
      return;
    }

    if (audio.paused) {
      setIsPlaying(true);
      void audio.play();
    } else {
      audio.pause();
      setIsPlaying(false);
    }
  };

  const handleTimeUpdate = () => {
    if (!audioRef.current) {
      return;
    }
    setCurrentTime(audioRef.current.currentTime);
  };

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) {
      return;
    }

    setCurrentTime(0);
    setDuration(0);

    audio.load();
    setIsPlaying(true);
    void audio.play();
  }, [currentTrackIndex]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) {
      return;
    }

    const handleEnded = async () => {
      if (!audioRef.current) {
        return;
      }
      setCurrentTime(audioRef.current.duration || 0);
      setCurrentTrackIndex((prev) => (prev + 1) % tracks.length);
      setIsPlaying(false);
      setIsTrackFinished(true);
      if (currentTrack) {
        trigger(String(currentTrack.id));
        await getMediaReward();
      }
    };

    audio.addEventListener('ended', handleEnded);

    return () => {
      audio.removeEventListener('ended', handleEnded);
    };
  }, [tracks, currentTrack, trigger, t]);

  const changeTrack = (index: number) => {
    telegramVibrate();
    setCurrentTrackIndex(index);
  };

  const getMediaReward = async () => {
    if (
      typeof currentTrack === 'undefined' ||
      currentTrack === null ||
      typeof settlementId === 'undefined'
    ) {
      return;
    }

    try {
      const resp = await claimReward({
        settlementId,
        activityId: currentTrack.id,
      }).unwrap();
      if (!resp.data) {
        throw new Error('claimAudioReward error');
      }

      const { cookiesAwarded, xpAwarded } = resp.data;

      notification.success(
        cookiesAwarded
          ? t('tasks.success', { reward: cookiesAwarded })
          : t('tasks.success_only_xp', { reward: xpAwarded })
      );
    } catch (error: unknown) {
      await log({ level: 'error', message: 'claimAudioReward error', error });
    }
  };

  return {
    audioRef,
    currentTime,
    currentTrack,
    changeTrack,
    handleLoaded,
    handleTimeUpdate,
    togglePlay,
    duration,
    isPlaying,
    currentTrackIndex,
    isTrackFinished,
    setIsTrackFinished,
    getMediaReward,
  };
};
